import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Backdrop } from '@mui/material';
import { AtlasState, useAtlasStore } from '../../../store/atlasStore';
import { SparkFlow, SparkJob } from '../types/sparkTypes';
import styles from '../styles/GlobalJobPanel.module.css';
import { JobDetailModal } from './modals/JobDetailModal';
import { analyzeParallelProgress, formatParallelStatus, getJobDisplayStatus } from '../hooks/useAnimatedProgress';
import { JobProgressBar } from './shared/JobProgressBar';
import { getJobActionLabel, getJobResultSummary } from '../utils/actionPresentation';
import { CreativeScopeContext, isJobRelevantToCreative } from '../utils/jobScope';
import { getJobResultNavigation, JobNavigationDeps } from '../utils/jobNavigation';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled']);

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return `${days}d ago`;
};

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path fill="currentColor" d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
  </svg>
);

const getActiveJobSummary = (job: SparkJob): string => {
  const { latestUpdate, parallelInfo } = analyzeParallelProgress(job._status_updates);
  if (parallelInfo) {
    return `${parallelInfo.stageName} (${formatParallelStatus(parallelInfo)})`;
  }
  if (latestUpdate?.message) {
    return latestUpdate.message;
  }
  return getJobDisplayStatus(job) === 'pending' ? 'Waiting to start...' : 'Processing...';
};

const getJobTimelineTimestamp = (job: SparkJob): string => {
  if (TERMINAL_STATUSES.has(getJobDisplayStatus(job) || job._status)) {
    return job._completed_at || job._created_at;
  }
  return job._created_at;
};

export interface ActivityPanelProps extends JobNavigationDeps {
  isOpen: boolean;
  onClose: () => void;
  scope?: CreativeScopeContext | null;
  scopeLabel?: string | null;
  onShowAll?: () => void;
}

export const ActivityPanel: React.FC<ActivityPanelProps> = ({
  isOpen,
  onClose,
  scope = null,
  scopeLabel,
  onShowAll,
  navigateToStep,
  openCreativeEditor,
  resolveIteration,
  resolveFeedbackIdForJob,
  scrollToFeedback,
}) => {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedJobFqn, setSelectedJobFqn] = useState<string | null>(null);
  const [sessionSeenJobFqns, setSessionSeenJobFqns] = useState<string[]>([]);
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const scrollTopRef = useRef<number>(0);
  const pendingSeenRef = useRef<Set<string>>(new Set());
  const flushSeenTimeoutRef = useRef<number | null>(null);

  const jobs = useAtlasStore((state: AtlasState) => state.currentSparkJobs);
  const flow = useAtlasStore((state: AtlasState) => state.currentSparkFlow);
  const markSparkJobAsSeen = useAtlasStore((state: AtlasState) => state.markSparkJobAsSeen);

  const baseJobs = useMemo(() => {
    if (!scope) {
      return jobs;
    }
    return jobs.filter((job) => isJobRelevantToCreative(job, scope));
  }, [jobs, scope]);

  const sessionSeenSet = useMemo(() => new Set(sessionSeenJobFqns), [sessionSeenJobFqns]);

  useEffect(() => {
    if (isOpen) {
      setStatusFilter('all');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSessionSeenJobFqns([]);
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollTopRef.current = scrollContainerRef.current.scrollTop;
    }
  }, [baseJobs, isOpen, statusFilter, sessionSeenSet]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollTopRef.current;
    }
  }, [baseJobs, isOpen, statusFilter, sessionSeenSet]);

  useEffect(() => {
    return () => {
      if (flushSeenTimeoutRef.current) {
        window.clearTimeout(flushSeenTimeoutRef.current);
      }
    };
  }, []);

  const flushSeenJobs = useCallback(() => {
    if (flushSeenTimeoutRef.current) {
      window.clearTimeout(flushSeenTimeoutRef.current);
      flushSeenTimeoutRef.current = null;
    }

    const jobFqns = Array.from(pendingSeenRef.current);
    pendingSeenRef.current.clear();
    if (jobFqns.length > 0) {
      void markSparkJobAsSeen(jobFqns);
    }
  }, [markSparkJobAsSeen]);

  const queueSeenJob = useCallback((jobFqn: string) => {
    pendingSeenRef.current.add(jobFqn);
    setSessionSeenJobFqns((current) => (current.includes(jobFqn) ? current : [...current, jobFqn]));

    if (flushSeenTimeoutRef.current) {
      return;
    }

    flushSeenTimeoutRef.current = window.setTimeout(() => {
      flushSeenJobs();
    }, 150);
  }, [flushSeenJobs]);

  const filteredJobs = useMemo(() => {
    return baseJobs
      .filter((job) => {
        if (statusFilter === 'all') {
          return true;
        }
        return getJobDisplayStatus(job) === statusFilter;
      })
      .sort((a, b) => new Date(getJobTimelineTimestamp(b)).getTime() - new Date(getJobTimelineTimestamp(a)).getTime());
  }, [baseJobs, statusFilter]);

  useEffect(() => {
    if (!isOpen || !scrollContainerRef.current) {
      return;
    }

    const root = scrollContainerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
            return;
          }

          const jobFqn = entry.target.getAttribute('data-job-fqn');
          if (!jobFqn) {
            return;
          }

          queueSeenJob(jobFqn);
        });
      },
      { root, threshold: [0.5] }
    );

    const items = root.querySelectorAll<HTMLElement>('[data-track-terminal-visibility="true"]');
    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, [filteredJobs, isOpen, queueSeenJob]);

  const handleJobClick = useCallback((job: SparkJob) => {
    if (!job._completion_seen_by_current_user && TERMINAL_STATUSES.has(getJobDisplayStatus(job) || job._status)) {
      queueSeenJob(job.fqn);
    }
    setSelectedJobFqn(job.fqn);
  }, [queueSeenJob]);

  const handleClose = useCallback(() => {
    flushSeenJobs();
    onClose();
  }, [flushSeenJobs, onClose]);

  const handleOpenResult = useCallback(async (job: SparkJob) => {
    const navigation = getJobResultNavigation(job, {
      navigateToStep,
      openCreativeEditor,
      resolveIteration,
      resolveFeedbackIdForJob,
      scrollToFeedback,
    });

    if (!navigation) {
      return;
    }

    if (!job._completion_seen_by_current_user) {
      queueSeenJob(job.fqn);
      flushSeenJobs();
    }

    await navigation.action();
    handleClose();
  }, [
    flushSeenJobs,
    handleClose,
    navigateToStep,
    openCreativeEditor,
    queueSeenJob,
    resolveFeedbackIdForJob,
    resolveIteration,
    scrollToFeedback,
  ]);

  const renderJobItem = useCallback((job: SparkJob, currentFlow: SparkFlow | null) => {
    const displayStatus = getJobDisplayStatus(job) || job._status;
    const navigation = getJobResultNavigation(job, {
      navigateToStep,
      openCreativeEditor,
      resolveIteration,
      resolveFeedbackIdForJob,
      scrollToFeedback,
    });
    const shouldTrackVisibility =
      !job._completion_seen_by_current_user &&
      !sessionSeenSet.has(job.fqn) &&
      TERMINAL_STATUSES.has(displayStatus);

    return (
      <li
        key={job.fqn}
        className={styles.jobItem}
        data-job-fqn={job.fqn}
        data-track-terminal-visibility={shouldTrackVisibility ? 'true' : 'false'}
        onClick={() => handleJobClick(job)}
      >
        <div className={styles.jobItemHeader}>
          <span className={styles.jobLabel}>
            {getJobActionLabel(job, currentFlow)}
            {!job._completion_seen_by_current_user && !sessionSeenSet.has(job.fqn) && TERMINAL_STATUSES.has(displayStatus) && (
              <span
                className={`${styles.unseenDot} ${displayStatus === 'completed' ? styles.unseenDotSuccess : styles.unseenDotFailure}`}
                title="New result"
              />
            )}
          </span>
          <span className={styles.jobTimestamp}>{formatRelativeTime(getJobTimelineTimestamp(job))}</span>
        </div>
        {displayStatus === 'pending' || displayStatus === 'running' ? (
          <>
            <div className={styles.jobSummary}>{getActiveJobSummary(job)}</div>
            <JobProgressBar job={job} variant="panel" hideStatus />
          </>
        ) : (
          <div className={styles.jobFooter}>
            <span className={styles.jobSummary}>{getJobResultSummary(job, currentFlow)}</span>
            {navigation && (
              <button
                className={styles.viewResultLink}
                onClick={(event) => {
                  event.stopPropagation();
                  void handleOpenResult(job);
                }}
              >
                {navigation.label}
              </button>
            )}
          </div>
        )}
      </li>
    );
  }, [
    handleJobClick,
    handleOpenResult,
    navigateToStep,
    openCreativeEditor,
    resolveFeedbackIdForJob,
    resolveIteration,
    scrollToFeedback,
    sessionSeenSet,
  ]);

  return (
    <>
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer }}
        open={isOpen}
        onClick={handleClose}
      />

      <div
        className={`${styles.jobPanelSlideout} ${isOpen ? styles.isOpen : ''}`}
        style={{ zIndex: 1201 }}
      >
        <div className={styles.panelContent}>
          <div className={styles.panelHeader}>
            <div className={styles.panelHeaderText}>
              <h3>Activity</h3>
              {scope && (
                <div className={styles.scopeBanner}>
                  <span>Showing jobs for {scopeLabel || 'this creative'}</span>
                  {onShowAll && (
                    <button
                      className={styles.scopeAction}
                      onClick={onShowAll}
                      type="button"
                    >
                      Show all
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className={styles.controls}>
              <div className={styles.filterControl}>
                <FilterIcon />
                <select
                  value={statusFilter}
                  onChange={(event) => {
                    setStatusFilter(event.target.value);
                  }}
                >
                  <option value="all">All statuses</option>
                  <option value="running">Running</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
          <ul className={styles.jobList} ref={scrollContainerRef}>
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job) => renderJobItem(job, flow))
            ) : (
              <li className={styles.noJobs}>No jobs match this view.</li>
            )}
          </ul>
        </div>
      </div>

      <JobDetailModal
        isOpen={selectedJobFqn !== null}
        onClose={() => setSelectedJobFqn(null)}
        jobFqn={selectedJobFqn}
      />
    </>
  );
};

export default ActivityPanel;
